/**
 * CASE.SHE — Відео hero.
 *
 * Мінімальний контролер фонового відео для секції `case-she-video-hero`:
 *   - тримає лише один активний <video> і підставляє джерела з <template>,
 *     тому desktop- і mobile-відео ніколи не завантажуються одночасно;
 *   - перемикає медіа та мобільні тексти на breakpoint без дублювання в DOM;
 *   - поважає `prefers-reduced-motion` і заблокований autoplay — постер лишається;
 *     відео декоративне й не має видимих елементів керування;
 *   - прибирає всі listeners та observers у `disconnectedCallback`,
 *     що покриває видалення й перезавантаження секції в Theme Editor.
 */

const DESKTOP_QUERY = '(min-width: 750px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class CaseSheVideoHero extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** Джерело, яке зараз підставлене у <video>. */
  #activeSource = '';

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.videoElement = this.querySelector('[data-cs-vhero-video]');
    this.headingElement = this.querySelector('[data-cs-vhero-title]');
    this.textElement = this.querySelector('[data-cs-vhero-text]');

    // Desktop-копія — це те, що вже відрендерив Liquid.
    this.desktopHeading = this.headingElement ? this.headingElement.textContent : '';
    this.desktopText = this.textElement ? this.textElement.innerHTML : '';

    this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
    this.motionQuery = window.matchMedia(REDUCED_MOTION_QUERY);

    this.desktopQuery.addEventListener('change', this.#sync, { signal });
    this.motionQuery.addEventListener('change', this.#sync, { signal });

    if (this.videoElement) {
      this.videoElement.addEventListener('playing', this.#handlePlaying, { signal });
      this.videoElement.addEventListener('pause', this.#handlePause, { signal });
      this.videoElement.addEventListener('error', this.#handleError, { signal, capture: true });
    }

    this.#sync();
    this.#observe();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = undefined;
    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#releaseVideo();

    // Скидаємо стан екземпляра: у Theme Editor перетягування секції може
    // повторно під'єднати той самий елемент, і медіа має ініціалізуватись знову.
    this.#activeSource = '';
    this.dataset.playing = 'false';
  }

  /** Перемальовує медіа та тексти під поточний breakpoint. */
  #sync = () => {
    const isDesktop = this.desktopQuery.matches;
    this.#syncCopy(isDesktop);
    this.#syncVideo(isDesktop ? 'desktop' : 'mobile');
  };

  /**
   * Порожні мобільні поля успадковують desktop-контент.
   * Тексти зберігаються в data-атрибутах, тож у DOM завжди лише один заголовок.
   *
   * @param {boolean} isDesktop
   */
  #syncCopy(isDesktop) {
    const mobileHeading = this.dataset.titleMobile;
    const mobileText = this.dataset.textMobile;

    if (this.headingElement && mobileHeading) {
      this.headingElement.textContent = isDesktop ? this.desktopHeading : mobileHeading;
    }

    if (this.textElement && mobileText) {
      this.textElement.innerHTML = isDesktop ? this.desktopText : mobileText;
    }
  }

  /**
   * @param {'desktop' | 'mobile'} key
   */
  #syncVideo(key) {
    if (!this.videoElement) return;

    const template =
      this.querySelector('template[data-cs-vhero-source="both"]') ||
      this.querySelector(`template[data-cs-vhero-source="${key}"]`);
    const nextSource = template ? String(template.dataset.csVheroSource) : 'none';

    if (nextSource === this.#activeSource) return;
    this.#activeSource = nextSource;

    this.videoElement.pause();

    if (!template) {
      this.#releaseVideo();
      this.dataset.hasVideo = 'false';
      this.dataset.playing = 'false';
      return;
    }

    this.videoElement.replaceChildren(template.content.cloneNode(true));
    this.videoElement.muted = true;
    // Нативний autoplay вмикається лише тоді, коли рух не обмежено налаштуваннями.
    this.videoElement.autoplay = !this.motionQuery.matches;
    this.videoElement.load();

    this.dataset.hasVideo = 'true';
    this.dataset.playing = 'false';
    this.#play();
  }

  /** Автовідтворення без звуку; за reduced motion лишається постер. */
  #play() {
    if (!this.videoElement || this.#activeSource === 'none') return;

    if (this.motionQuery.matches) {
      this.#setState('paused');
      return;
    }

    this.videoElement.muted = true;
    const played = this.videoElement.play();

    if (played && typeof played.catch === 'function') {
      // Заблокований autoplay не є помилкою: постер лишається видимим.
      played.catch(() => this.#setState('paused'));
    }
  }

  #releaseVideo() {
    if (!this.videoElement) return;
    this.videoElement.pause();
    this.videoElement.replaceChildren();
    this.videoElement.removeAttribute('src');
    this.videoElement.load();
  }

  /** Пауза поза екраном, поновлення при поверненні у видиму область. */
  #observe() {
    if (typeof IntersectionObserver === 'undefined') return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!this.videoElement || this.#activeSource === 'none') continue;
          if (entry.isIntersecting) {
            this.#play();
          } else {
            this.videoElement.pause();
          }
        }
      },
      { threshold: 0 }
    );

    this.#observer.observe(this);
  }

  #handlePlaying = () => {
    this.#setState('playing');
  };

  #handlePause = () => {
    this.#setState('paused');
  };

  #handleError = () => {
    // Відео не завантажилось — лишаємо постер видимим.
    this.dataset.playing = 'false';
    this.dataset.hasVideo = 'false';
  };

  /**
   * `data-playing` керує лише видимістю постера над відео.
   *
   * @param {'playing' | 'paused'} state
   */
  #setState(state) {
    this.dataset.playing = state === 'playing' ? 'true' : 'false';
  }
}

if (!customElements.get('case-she-video-hero')) {
  customElements.define('case-she-video-hero', CaseSheVideoHero);
}
